import { Injectable } from '@angular/core';
import {Observable} from "rxjs";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {tap} from "rxjs/operators";
import {BackendService} from "../../services/backend.service";
import {DebugService} from "../../debug/debug.service";
import {HeizcheckRequestService} from "./heizcheck-request.service";

/**
 * global request id that can be injected by the backend.
 * @var string
 */
declare let advisorRequestId;

/**
 * Service for dealing with the backend and make request to the backend.
 */
@Injectable({
  providedIn: 'root'
})
export class HeizcheckBackendService extends BackendService {
  /**
   * name of the id if it comes from the request.
   */
  public static readonly PARAM_NAME_REQUESTID = 'id';

  /**
   * @inheritDoc
   */
  requestIdName = 'hec-requestid';

  /**
   * @inheritdoc
   */
  backendDataEndpoint = '/hec/data/';

  /**
   * Indicator if the request id was in the request
   * @private
   */
  private readonly requestIdInUrl: boolean = false;

  /**
   * remember the last data that we saved to avoid too much performance need on saving requests
   */
  lastData: string;

  /**
   * constructor.
   * @param http
   * @param backendService
   * @param debugService
   */
  constructor(protected http: HttpClient, private backendService: BackendService, protected debugService: DebugService) {
    super(http, debugService);

    // check if there is a request ID that should be injected
    if(typeof advisorRequestId === "string" && advisorRequestId.length > 0) {
      this.requestId = advisorRequestId;
      this.saveRequestId();
    }

    // check if we have the ID in the request, if yes save it
    if(URLSearchParams) {
      const urlParams = new URLSearchParams(document.location.search);
      if (urlParams.has(HeizcheckBackendService.PARAM_NAME_REQUESTID) && urlParams.get(HeizcheckRequestService.PARAM_NAME_REQUESTID).match(/[a-z]{3}[a-z0-9.]{23}/)) {
        this.requestId = urlParams.get(HeizcheckRequestService.PARAM_NAME_REQUESTID);
        this.requestIdInUrl = true;
        this.saveRequestId();
      }
    }
  }

  /**
   * indicates if the id was in the request
   */
  public isRequestIdInUrl(): boolean {
    return this.requestIdInUrl;
  }

  /**
   * @inheritDoc
   * this is a small workaround so other components can still access the generic backendservice and will get the real advisor name
   * @param advisor
   */
  setAdvisor(advisor: string) {
    super.setAdvisor(advisor);

    this.backendService.setAdvisor(advisor);
  }

  /**
   * starts the calculation of the first chapter with the backend.
   * @param input
   * @param requestId
   * @param invoiceIndex
   */
  verbrauchsanalyse(input: any, requestId: string, invoiceIndex: number = null): Observable<any>{
    let url = '/hec/verbrauchsanalyse/' + requestId;
    if(invoiceIndex != null) {
      url += ('?invoice=' + invoiceIndex);
    }

    // add debug header if we are in debug mode
    const headers = new HttpHeaders({
      'x-api-debug': this.debugService.isDebugOn() ? '1' : '0'
    });

    return this.http.post(url, input, {headers}).pipe(
      tap(data => {
        if(data.debug) {
          this.addDebug(data.debug);
        }
      })
    );
  }

  /**
   * starts the calculation building consumption based on a heating bill.
   * @param input
   * @param requestId
   */
  verbrauchsanalyseHeizkostenabrechnung(input: any, requestId: string): Observable<any>{
    const url = '/hec/verbrauchsanalyse-abrechnung/' + requestId;

    // add debug header if we are in debug mode
    const headers = new HttpHeaders({
      'x-api-debug': this.debugService.isDebugOn() ? '1' : '0'
    });

    return this.http.post(url, input, {headers}).pipe(
      tap(data => {
        if(data.debug) {
          this.addDebug(data.debug);
        }
      })
    );
  }

  /**
   * starts the analysis of a heating bill
   * @param input
   * @param requestId
   * @param count
   */
  heizkostenanalyse(input: any, requestId: string, count: boolean = false): Observable<any>{
    let url = '/hec/heizkostenanalyse/' + requestId;
    if(count === true) {
      url += '?count=1';
    }

    return this.http.post(url, input).pipe(
      tap(data => {
        if(data.debug) {
          this.addDebug(data.debug);
        }
      })
    );
  }
}
